/**
 * Smart content detection utility for task recommendations
 * Detects when tasks mention document, image, or research generation opportunities
 */

export type ContentType = 'document' | 'image' | 'research';

// Legacy interface for backward compatibility
interface ContentDetection {
  hasDocument: boolean;
  hasImage: boolean;
  hasResearch: boolean;
  detectedTypes: ContentType[];
}

// Enhanced interface that includes generated content awareness
export interface ContentDetectionResult {
  detectedTypes: ContentType[];
  primaryType: ContentType | null;
  hasGeneratedContent: {
    document: boolean;
    image: boolean;
    research: boolean;
  };
  shouldShowGenerateButtons: boolean;
  shouldShowViewButtons: boolean;
}

// Task interface (imported from types but redefined for clarity)
interface TaskForDetection {
  title: string;
  description?: string;
  generated_document_id?: string | null;
  generated_image_id?: string | null;
  generated_research_id?: string | null;
}

// Keywords that indicate document generation opportunities
const DOCUMENT_KEYWORDS = [
  'document', 'report', 'proposal', 'contract', 'agreement',
  'article', 'blog post', 'newsletter', 'guide', 'manual',
  'email', 'letter', 'memo', 'brief', 'summary',
  'presentation', 'slide deck', 'pitch', 'outline',
  'template', 'checklist', 'workflow', 'script',
  'campaign', 'ad copy', 'social media post', 'caption',
  'press release', 'announcement', 'testimonial', 'review',
  'strategy', 'plan', 'framework', 'process',
  'white paper', 'case study', 'data sheet',
  'blueprint', 'create', 'write', 'draft', 'compose',
  'develop', 'build', 'generate', 'produce'
];

// Keywords that indicate image generation opportunities
const IMAGE_KEYWORDS = [
  'art', 'artwork', 'illustration', 'graphic', 'icon',
  'photo', 'image', 'picture', 'visual', 'mockup',
  'video', 'animation', 'gif', 'clip',
  'logo', 'banner', 'poster', 'flyer', 'infographic',
  'drawing', 'sketch', 'render',
  'thumbnail', 'cover', 'header', 'background',
  'illustrate', 'capture', 'draw'
];

// Keywords that indicate research generation opportunities
const RESEARCH_KEYWORDS = [
  'research', 'analysis', 'study', 'investigation', 'findings',
  'market analysis', 'competitor review', 'survey', 'assessment',
  'competitive analysis', 'market research', 'industry analysis',
  'customer research', 'user research', 'trend analysis',
  'swot analysis', 'feasibility study', 'market survey',
  'benchmark', 'comparison', 'evaluation', 'audit',
  'explore', 'investigate', 'analyze', 'examine', 'review',
  'discover', 'identify', 'understand', 'learn about',
  'data collection', 'insights', 'statistics', 'metrics',
  'case study', 'best practices', 'methodology', 'approach',
  'landscape analysis', 'deep dive', 'research report',
  'intelligence gathering', 'fact-finding', 'due diligence'
];

/**
 * Detects content generation opportunities in task text with position-based priority
 * Earlier words have higher priority, only returns the highest priority type
 */
export function detectContentTypes(taskTitle: string, taskDescription: string = ''): ContentDetection {
  const combinedText = `${taskTitle} ${taskDescription}`.toLowerCase();
  
  // Find all matches with their positions and types
  const matches: { type: ContentType; position: number; keyword: string }[] = [];
  
  // Check document keywords
  DOCUMENT_KEYWORDS.forEach(keyword => {
    const position = combinedText.indexOf(keyword.toLowerCase());
    if (position !== -1) {
      matches.push({ type: 'document', position, keyword });
    }
  });
  
  // Check image keywords (with stricter matching)
  IMAGE_KEYWORDS.forEach(keyword => {
    const position = combinedText.indexOf(keyword.toLowerCase());
    if (position !== -1) {
      // Stricter matching for "visual" - must be exact word, not part of "visually"
      if (keyword === 'visual') {
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (regex.test(combinedText)) {
          matches.push({ type: 'image', position, keyword });
        }
      } else {
        matches.push({ type: 'image', position, keyword });
      }
    }
  });
  
  // Check research keywords
  RESEARCH_KEYWORDS.forEach(keyword => {
    const position = combinedText.indexOf(keyword.toLowerCase());
    if (position !== -1) {
      matches.push({ type: 'research', position, keyword });
    }
  });
  
  // Sort by position (earlier = higher priority)
  matches.sort((a, b) => a.position - b.position);
  
  // Get the first (highest priority) match
  const primaryMatch = matches[0];
  
  // Legacy boolean properties for backward compatibility
  const hasDocument = matches.some(m => m.type === 'document');
  const hasImage = matches.some(m => m.type === 'image');
  const hasResearch = matches.some(m => m.type === 'research');
  
  // Only return the highest priority type
  const detectedTypes: ContentType[] = primaryMatch ? [primaryMatch.type] : [];
  
  return {
    hasDocument,
    hasImage,
    hasResearch,
    detectedTypes
  };
}

/**
 * Enhanced content detection with generated content awareness
 * First checks if content already exists, then falls back to keyword detection
 */
export function detectContentTypesWithGenerated(task: TaskForDetection): ContentDetectionResult {
  // Check what content has already been generated
  const hasGeneratedContent = {
    document: !!task.generated_document_id,
    image: !!task.generated_image_id,
    research: !!task.generated_research_id,
  };

  const hasAnyGenerated = Object.values(hasGeneratedContent).some(Boolean);

  // If we have generated content, show View buttons
  if (hasAnyGenerated) {
    const generatedTypes: ContentType[] = [];
    if (hasGeneratedContent.document) generatedTypes.push('document');
    if (hasGeneratedContent.image) generatedTypes.push('image');
    if (hasGeneratedContent.research) generatedTypes.push('research');

    return {
      detectedTypes: generatedTypes,
      primaryType: generatedTypes[0] || null,
      hasGeneratedContent,
      shouldShowGenerateButtons: false,
      shouldShowViewButtons: true,
    };
  }

  // No generated content exists, use keyword detection for Generate buttons
  const keywordDetection = detectContentTypes(task.title, task.description || '');
  
  return {
    detectedTypes: keywordDetection.detectedTypes,
    primaryType: keywordDetection.detectedTypes[0] || null,
    hasGeneratedContent,
    shouldShowGenerateButtons: keywordDetection.detectedTypes.length > 0,
    shouldShowViewButtons: false,
  };
}

/**
 * Gets user-friendly label for content type
 */
export function getContentTypeLabel(type: ContentType): string {
  switch (type) {
    case 'document':
      return 'Generate Document';
    case 'image':
      return 'Generate Image';
    case 'research':
      return 'Generate Research';
    default:
      return 'Generate Content';
  }
}

/**
 * Gets appropriate icon component name for content type
 */
export function getContentTypeIcon(type: ContentType): string {
  switch (type) {
    case 'document':
      return 'FileText';
    case 'image':
      return 'Image';
    case 'research':
      return 'Search';
    default:
      return 'Plus';
  }
} 